<template>
  <FriedModal :closable="!validating || validated" @close="$emit('close')">
    <template #header>
      <FriedModalHeader
        :header="t('my_document_auth_modal_header')"
        :description="creatededEmail ? '' : t('my_document_auth_modal_description', { email })"
      />
    </template>
    <FriedMessage
      v-if="showErrorMessage"
      class="error-message"
      type="error"
      :title="$t('something-went-wrong')"
    >
      <i18n-t keypath="try_again_or_contact_support_with_link">
        <template #contact_support>
          <FriedLink href="https://help.getaccept.com/" target="_blank">
            {{ t('link_contact_support') }}
          </FriedLink>
        </template>
      </i18n-t>
    </FriedMessage>
    <div v-if="creatededEmail">
      <FriedParagraph class="info-message">
        {{ t('my_document_auth_modal_sent_description', { email }) }}
      </FriedParagraph>
      <FriedLabel for="pin">{{ t('my_document_auth_modal_pin_label') }}</FriedLabel>
      <Pin
        id="pin"
        data-external="my-document-validation-modal-pin-code-input"
        :pin-length="pinLength"
        :error="isInvalidCode"
        @pin="pin = $event"
      />
      <div class="help-message">
        <FriedHelpMessage v-if="isInvalidCode" error>
          <i18n-t keypath="could_not_verify_code">
            <template #resend_code_link>
              <FriedLink target="_blank" @click="createEmail">
                {{ t('resend_code_link') }}
              </FriedLink>
            </template>
          </i18n-t>
        </FriedHelpMessage>
        <template v-else>
          {{ t('my_document_auth_modal_help_message') }}
          <FriedLink @click="createEmail">{{ t('resend_code') }}</FriedLink>
        </template>
      </div>
    </div>
    <template #footer>
      <FriedModalButtonsFooter>
        <FriedButton
          button-type="secondary"
          data-external="my-document-validation-modal-cancel-button"
          :disabled="creatingEmail || validating || validated"
          @click="$emit('close')"
        >
          {{ t('cancel') }}
        </FriedButton>
        <FriedButton
          v-if="creatededEmail"
          :loading="validating"
          :loaded="validated"
          :disabled="pin.length !== pinLength"
          data-external="my-document-validation-modal-authenticate-button"
          @click="validateEmail(+pin)"
        >
          {{ t('verify') }}
        </FriedButton>
        <FriedButton
          v-else
          :loading="creatingEmail"
          data-external="my-document-validation-modal-send-code-button"
          @click="createEmail"
        >
          {{ t('send_code') }}
        </FriedButton>
      </FriedModalButtonsFooter>
    </template>
  </FriedModal>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import Pin from '@getaccept/lib-shared/src/components/Pin.vue';
import { storeToRefs } from 'pinia';
import { useMyDocumentsCreateEmail } from '../../my-documents/composables/my-documents-create-email.composable';
import { useMyDocumentsValidateEmail } from '../../my-documents/composables/my-documents-validate-email.composable';
import { useUserStore } from '../../user/store/user.store';
import { useMyDocumentsAuth } from '../../my-documents/composables/my-documents-auth.composable';

defineEmits<{ close: [] }>();

const { user } = storeToRefs(useUserStore());

const {
  mutateAsync: validateEmail,
  isPending: validating,
  isSuccess: validated,
  isError: validateError,
  isInvalidCode,
} = useMyDocumentsValidateEmail();

useMyDocumentsAuth(
  computed(() => user.value?.id),
  computed(() => validated.value)
);
const pin = ref('');

watch(pin, (newVal, oldVal) => {
  if (oldVal.length < 6 && newVal.length === 6) {
    validateEmail(+pin.value);
  }
});

const email = computed(() => user.value?.email || '');

const {
  mutateAsync: createEmail,
  isPending: creatingEmail,
  isSuccess: creatededEmail,
  isError: createEmailError,
} = useMyDocumentsCreateEmail();

const showErrorMessage = computed(
  () => createEmailError.value || (validateError.value && !isInvalidCode.value)
);

const pinLength = 6;
</script>

<style lang="scss" scoped>
.error-message {
  margin-bottom: var(--spacing-50);
}

.info-message {
  margin-bottom: var(--spacing-150);
}

.error {
  color: var(--error-dark);
}

.help-message {
  display: flex;
  align-items: center;
  gap: var(--spacing-25);
}

.full-width {
  width: 100%;
}
</style>
